/**
 * TeamOne Assistant — a lightweight in-app chatbot.
 *
 * It understands a handful of intents (check in / check out, leave, attendance
 * adjustment, "my hours", help) by keyword matching — no external LLM or API key
 * is needed, matching the app's existing rule-based assistants. Quick-action
 * chips do the same things with one tap.
 *
 * Actions are REAL: "check me in" runs the same attendance store call as the
 * check-in button (GPS included), and leave / adjustment requests open the right
 * page. To upgrade to free-form conversational AI later, route handleIntent()'s
 * `unknown` branch through a backend LLM proxy (see note in the code).
 */
import { secureRandom } from '@/lib/secureRandom';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Send, X, LogIn, LogOut, CalendarDays, Wrench, Clock } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../lib/utils';
import { getFastLocation } from '../lib/fastLocation';
import { useAttendanceStore, type AttendanceError } from '../store/attendanceStore';
import { useAuthStore } from '../store/authStore';

interface Msg { id: string; role: 'user' | 'bot'; text: string }

type Intent = 'checkin' | 'checkout' | 'leave' | 'adjustment' | 'status' | 'help' | 'unknown';

function detectIntent(raw: string): Intent {
    const q = raw.toLowerCase();
    if (/\b(check[\s-]?out|clock[\s-]?out|punch[\s-]?out|sign[\s-]?out|i'?m leaving|leaving now|end (my )?day|done for (the|today))\b/.test(q)) return 'checkout';
    if (/\b(check[\s-]?in|clock[\s-]?in|punch[\s-]?in|sign[\s-]?in|i'?m in|start (my )?day|start work)\b/.test(q)) return 'checkin';
    if (/\b(adjust|regulari[sz]|correct(ion)?|missed (a )?(check|punch)|forgot to (check|punch)|wrong time|fix (my )?attendance)\b/.test(q)) return 'adjustment';
    if (/\b(leave|vacation|time off|day off|apply.*leave|sick)\b/.test(q)) return 'leave';
    if (/\b(my hours|hours today|worked today|my attendance|am i checked|my status|how long)\b/.test(q)) return 'status';
    if (/\b(help|what can you|who are you|hi|hello|hey|hola)\b/.test(q)) return 'help';
    return 'unknown';
}

const uid = () => secureRandom().toString(36).slice(2);

export function AssistantChat() {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [msgs, setMsgs] = useState<Msg[]>([]);
    const navigate = useNavigate();
    const firstName = (useAuthStore((s) => s.user?.name) || '').trim().split(/\s+/)[0] || 'there';
    const { checkIn, checkOut, fetchToday } = useAttendanceStore();
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);

    // Greet once, when first opened.
    useEffect(() => {
        if (open && msgs.length === 0) {
            setMsgs([{ id: uid(), role: 'bot', text: `Hi ${firstName} 👋 I'm your TeamOne assistant. I can check you in or out, help with leave, or fix an attendance mistake. Try a quick action below, or just type what you need.` }]);
        }
    }, [open, msgs.length, firstName]);

    const push = (role: Msg['role'], text: string) => setMsgs((m) => [...m, { id: uid(), role, text }]);

    async function runCheckIn(): Promise<string> {
        const today = useAttendanceStore.getState().today;
        if (today?.state === 'CHECKED_IN') return "You're already checked in. Say \"check out\" when you're done.";
        try {
            const s = await checkIn(getFastLocation().then((f) => f ?? {}));
            const where = s.checkInLocation ? ` at ${s.checkInLocation}` : '';
            return `✅ You're checked in${where}. Have a great day!`;
        } catch (e) {
            return `I couldn't check you in: ${(e as AttendanceError).message}`;
        }
    }

    async function runCheckOut(): Promise<string> {
        const today = useAttendanceStore.getState().today;
        if (today?.state !== 'CHECKED_IN') return "You're not checked in right now, so there's nothing to check out of.";
        try {
            const s = await checkOut(getFastLocation().then((f) => f ?? {}));
            const where = s.checkOutLocation ? ` from ${s.checkOutLocation}` : '';
            return `✅ You're checked out${where}. See you next time!`;
        } catch (e) {
            return `I couldn't check you out: ${(e as AttendanceError).message}`;
        }
    }

    function statusText(): string {
        const today = useAttendanceStore.getState().today;
        if (!today) return 'I could not read your attendance right now — open the Attendance page to check.';
        const worked = today.summary?.workedMinutes ?? today.sessions.reduce((n, s) => n + (s.status !== 'OPEN' && s.status !== 'SUPERSEDED' ? s.creditedMinutes || 0 : 0), 0);
        const h = `${Math.floor(worked / 60)}h ${String(worked % 60).padStart(2, '0')}m`;
        if (today.state === 'CHECKED_IN') return `You're checked in. You've logged about ${h} so far today.`;
        if (today.state === 'CHECKED_OUT') return `You're checked out. Total today: ${h} across ${today.sessions.filter((s) => s.status !== 'SUPERSEDED').length} session(s).`;
        return "You haven't checked in yet today. Say \"check in\" whenever you're ready.";
    }

    async function handleIntent(intent: Intent): Promise<string> {
        switch (intent) {
            case 'checkin': return runCheckIn();
            case 'checkout': return runCheckOut();
            case 'status': return statusText();
            case 'leave':
                navigate('/people/leave');
                return 'Opening the Leave page — you can apply for or review leave there.';
            case 'adjustment':
                navigate('/people/attendance?tab=adjustments');
                return 'Opening your attendance adjustments — use "Request adjustment" to fix a missed check-in/out or a wrong time.';
            case 'help':
                return 'I can help you with:\n• "Check me in" / "Check me out"\n• "My hours today"\n• "Apply for leave"\n• "Fix my attendance" (adjustment request)\nJust type it, or use the buttons below.';
            // To add real conversational AI, send `unknown` queries to a backend
            // endpoint that calls an LLM (e.g. Anthropic Claude) and returns a reply.
            default:
                return "I'm not sure about that yet. I can check you in or out, show your hours, or help with leave and attendance fixes — try one of the buttons below.";
        }
    }

    async function send(text: string) {
        const trimmed = text.trim();
        if (!trimmed || busy) return;
        push('user', trimmed);
        setInput('');
        setBusy(true);
        try {
            const reply = await handleIntent(detectIntent(trimmed));
            push('bot', reply);
            fetchToday().catch(() => undefined);
        } finally {
            setBusy(false);
        }
    }

    const quick: { label: string; icon: React.ElementType; text: string }[] = [
        { label: 'Check in', icon: LogIn, text: 'check in' },
        { label: 'Check out', icon: LogOut, text: 'check out' },
        { label: 'My hours', icon: Clock, text: 'my hours today' },
        { label: 'Leave', icon: CalendarDays, text: 'apply for leave' },
        { label: 'Fix attendance', icon: Wrench, text: 'fix my attendance' },
    ];

    return (
        <>
            {/* Launcher */}
            {!open && (
                <Button
                    onClick={() => setOpen(true)}
                    aria-label="Open assistant"
                    className="fixed bottom-5 right-5 z-50 size-14 rounded-full p-0 shadow-lg [&_svg]:size-6"
                >
                    <Bot />
                </Button>
            )}

            {/* Panel */}
            {open && (
                <div className="fixed bottom-5 right-5 z-50 flex h-[32rem] max-h-[calc(100vh-2.5rem)] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-lg animate-scale-in">
                    {/* Header */}
                    <div className="flex items-center gap-2.5 bg-sidebar px-4 py-3 text-sidebar-foreground">
                        <span className="flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground [&_svg]:size-4"><Bot /></span>
                        <div className="min-w-0 flex-1">
                            <p className="font-display text-sm font-semibold leading-tight">TeamOne Assistant</p>
                            <p className="text-xs text-sidebar-muted">Here to help</p>
                        </div>
                        <Button variant="unstyled" size="unstyled" aria-label="Close assistant" onClick={() => setOpen(false)}
                            className="flex size-7 items-center justify-center rounded-md text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground [&_svg]:size-4">
                            <X />
                        </Button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 space-y-3 overflow-y-auto bg-background px-3 py-3">
                        {msgs.map((m) => (
                            <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                                <div className={cn('max-w-[85%] whitespace-pre-line rounded-2xl px-3 py-2 text-sm',
                                    m.role === 'user' ? 'rounded-br-sm bg-primary text-primary-foreground' : 'rounded-bl-sm bg-secondary text-foreground')}>
                                    {m.text}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="rounded-2xl rounded-bl-sm bg-secondary px-3 py-2 text-sm text-muted-foreground">…</div>
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>

                    {/* Quick actions */}
                    <div className="flex flex-wrap gap-1.5 border-t border-border bg-card px-3 py-2">
                        {quick.map((q) => (
                            <Button key={q.label} variant="secondary" size="xs" disabled={busy} onClick={() => send(q.text)} className="gap-1">
                                <q.icon /> {q.label}
                            </Button>
                        ))}
                    </div>

                    {/* Input */}
                    <form className="flex items-center gap-2 border-t border-border bg-card p-2.5"
                        onSubmit={(e) => { e.preventDefault(); send(input); }}>
                        <input
                            className="h-9 flex-1 rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30"
                            placeholder="Type a message…" value={input} disabled={busy}
                            onChange={(e) => setInput(e.target.value)} aria-label="Message the assistant" />
                        <Button type="submit" size="icon-sm" disabled={busy || !input.trim()} aria-label="Send"><Send /></Button>
                    </form>
                </div>
            )}
        </>
    );
}
